/**
 * One-pager templates — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const onePagerTemplatesPack: ToilePack = {
    slug: 'one-pager-templates',
    category: 'office',
    name: 'One-pager templates',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'project', label: 'Project', subject: 'a one-page project summary with goal, timeline and owner'},
        {key: 'product', label: 'Product', subject: 'a product one-pager with features and price'},
        {key: 'personal', label: 'Personal', subject: 'a personal one-pager bio'},
    ],
    styles: [
        {key: 'clean', label: 'Clean', style: 'clean one-page layout with a bold title, three evenly spaced blocks and thin rules', palette: 'black ink and one blue accent on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'colour', label: 'Colour header', style: 'one-page layout with a large coloured header block, icon circles and a coloured contact footer', palette: 'coral and charcoal on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'editorial', label: 'Editorial', style: 'editorial one-page layout with a big serif title, a narrow sidebar and pull-quote style highlights', palette: 'dark green and cream', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['dark mode', 'with chart', 'orange accent'],
    directionHint: 'try: dark mode',
    seo: {
        title: 'One-pager template: project, product, personal',
        metaDescription: 'Nine printable one-pager templates: a project, a product and a personal one-pager, each in clean, colour or editorial style.',
        h1: 'One-pager template: project, product, personal',
        tagline: 'Three one-page summaries, each in three styles, with a header and a few blocks.',
        intro: 'A one-pager is a single page that sums up a project, a product or a person. These nine show a project summary with goal, timeline and owner; a product one-pager with features and cost; and a personal one-pager bio. Only the Overview and Contact headings are lettered. Type a twist, such as dark mode, and it is a request.',
        useCases: [
            'a summary to send with a project proposal',
            'a sell sheet for a product',
            'a short bio to share with a group',
            'a one-page brief for a team meeting',
        ],
        faq: [
            {q: 'What goes in a one-pager?', a: 'A header and a few blocks. A project has its goal, timeline and owner, a product has its features, and a personal one-pager has a short bio.'},
            {q: 'What does the editorial style look like?', a: 'A big serif title, a narrow sidebar and pull-quote style highlights, on dark green and cream.'},
            {q: 'Can I add a chart?', a: 'Type it as a twist, such as with chart. It is a request for a chart, not a promised result.'},
            {q: 'Is the page ready to print?', a: 'It is a flat, print-ready sheet with an even margin.'},
        ],
    },
}
